import { atom } from 'jotai';
import { fetchApi } from '../../lib/apiClient';
import { ReminderMethodType, createDefaultReminderMethods } from '../../types/notificationMapping';

// 用户偏好设置接口
export interface UserPreferences {
  preferredClaudeModel?: string;
  timezoneOffset?: number;
  defaultReminderMethods?: ReminderMethodType[];
  selectedCharacterId?: string;
  preferredLanguage?: string;
  [key: string]: any;
}

// API 响应接口
interface UserPreferencesResponse {
  success?: boolean;
  preferences?: any;
  preferredClaudeModel?: string;
  timezoneOffset?: number;
  defaultReminderMethods?: ReminderMethodType[];
  selectedCharacterId?: string;
  preferredLanguage?: string;
  availableModels?: Array<{ name: string; description: string }>;
}

// 用户偏好状态原子
export const userPreferencesAtom = atom<UserPreferences | null>(null);

// 获取用户偏好的异步原子
export const fetchUserPreferencesAtom = atom(
  null,
  async (get, set) => {
    try {
      const response = await fetchApi('/profiles/me/preferences');
      const data: UserPreferencesResponse = response;
      
      const preferences: UserPreferences = {
        preferredClaudeModel: data.preferredClaudeModel,
        timezoneOffset: data.timezoneOffset,
        defaultReminderMethods: data.defaultReminderMethods || createDefaultReminderMethods(),
        selectedCharacterId: data.selectedCharacterId,
        preferredLanguage: data.preferredLanguage,
        // 保留原始preferences数据
        ...data.preferences,
      };
      
      set(userPreferencesAtom, preferences);
      return preferences;
    } catch (error) {
      console.error('Failed to fetch user preferences:', error);
      // 设置默认值
      const defaultPreferences: UserPreferences = {
        defaultReminderMethods: createDefaultReminderMethods(),
      };
      set(userPreferencesAtom, defaultPreferences);
      throw error;
    }
  }
);

// 更新用户偏好的异步原子
export const updateUserPreferencesAtom = atom(
  null,
  async (get, set, update: Partial<UserPreferences>) => {
    try {
      const response = await fetchApi('/profiles/me/preferences', {
        method: 'PUT',
        body: update,
      });
      
      const data: UserPreferencesResponse = response;
      
      // 更新本地状态
      const currentPreferences = get(userPreferencesAtom) || {};
      const updatedPreferences: UserPreferences = {
        ...currentPreferences,
        preferredClaudeModel: data.preferredClaudeModel ?? currentPreferences.preferredClaudeModel,
        timezoneOffset: data.timezoneOffset ?? currentPreferences.timezoneOffset,
        defaultReminderMethods: data.defaultReminderMethods ?? currentPreferences.defaultReminderMethods,
        selectedCharacterId: data.selectedCharacterId ?? currentPreferences.selectedCharacterId,
        preferredLanguage: data.preferredLanguage ?? currentPreferences.preferredLanguage,
      };
      
      set(userPreferencesAtom, updatedPreferences);
      return updatedPreferences;
    } catch (error) {
      console.error('Failed to update user preferences:', error);
      throw error;
    }
  }
);

// 获取用户默认提醒方式的派生原子
export const userDefaultReminderMethodsAtom = atom((get) => {
  const preferences = get(userPreferencesAtom);
  return preferences?.defaultReminderMethods || createDefaultReminderMethods();
});
